const isCapacitor = typeof (window as unknown as Record<string, unknown>).Capacitor !== "undefined";
const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : isCapacitor
    ? "https://api.ehille.org/api/v1"
    : "/api/v1";

let accessToken: string | null = localStorage.getItem("ehille_token");

export function setToken(token: string | null) {
  accessToken = token;
  if (token) localStorage.setItem("ehille_token", token);
  else localStorage.removeItem("ehille_token");
}

export function getToken() {
  return accessToken;
}

export async function api<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    // Only try token refresh for GET requests or /auth/me
    // Don't treat business 401s (wrong password on join etc.) as auth failures
    const isAuthCheck = path === "/auth/me" || (options.method ?? "GET") === "GET";

    if (isAuthCheck) {
      const refreshToken = localStorage.getItem("ehille_refresh");
      if (refreshToken) {
        try {
          const refreshRes = await fetch(`${API_BASE}/auth/refresh`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken }),
          });
          if (refreshRes.ok) {
            const data = await refreshRes.json() as { accessToken: string; refreshToken: string };
            setToken(data.accessToken);
            localStorage.setItem("ehille_refresh", data.refreshToken);
            headers["Authorization"] = `Bearer ${data.accessToken}`;
            const retry = await fetch(`${API_BASE}${path}`, { ...options, headers });
            if (!retry.ok) throw new Error("Retry failed");
            return retry.json() as T;
          }
        } catch {
          setToken(null);
          localStorage.removeItem("ehille_refresh");
          window.location.href = "/";
          throw new Error("Session expired");
        }
      }
    }

    // For non-auth 401s, just throw the error message
    const body = await res.json().catch(() => ({ error: "Unauthorized" })) as Record<string, unknown>;
    throw new Error((body.error as string) || "Unauthorized");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "Request failed" })) as Record<string, unknown>;
    if (res.status === 409 && body) {
      return body as T;
    }
    throw new Error((body.error as string) || "Request failed");
  }

  return res.json() as T;
}
